// CORE
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

// CUSTOM HOOKS
import { useAppSelector } from 'hooks/redux';
import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';

import { a2aTransfersActions, a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
import { tracker } from 'app-components/utils/core';
import * as fundingAccountsSelectors from 'data/fundingAccounts/selectors';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
// COMPONENTS
import { showInitiateTransferDialog } from 'components/Dialogs/A2A/actions';
import { transferDialogIndexEnums } from 'components/Dialogs/A2A/transferDialogIndexEnums';

import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';

import ZeroStateView from 'components/ZeroStateView';
// LOCAL
import TransferAccountSelect from './TransferAccountSelect';
import TransferList from './TransferList';

const useStyles = makeStyles()(() => ({
  root: {
    height: '100%',
    overflowY: 'hidden',
    width: '100%',
  },
  container: {
    height: '100%',
    width: '100%',
  },
  rightWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: 320,
    maxWidth: 450,
    minWidth: 360,
    flexGrow: 2,
    flexShrink: 1,
  },
  rightCardWrapper: {
    width: 'calc(100% - 24px)',
    marginBottom: 30,
    display: 'flex',
    flexWrap: 'no-wrap',
    marginRight: 40,
    flexDirection: 'column',
    minHeight: 350,
    maxHeight: 430,
  },
  zeroStyles: {
    alignSelf: 'center',
    display: 'flex',
    margin: 'auto 0',
  },
  errorRoot: {
    minWidth: 540,
    minHeight: 700,
    width: '100%',
    height: '100%',
    borderRadius: 20,
    display: 'flex',
    alignItems: 'flex-start',
    paddingBottom: 20,
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 2,
    flexBasis: '720px',
  },
}));

const kickoffObj = {
  initiator: 'transfer activity page',
};

const TransferHistory = (props) => {
  const { classes } = useStyles(props);
  const dispatch = useDispatch();

  //  The logic used to determine what dialog to open, email, identity, or transfer
  const kickoffA2AFlow = useKickoffA2AFlow();

  // STATES
  const [fromAccount, setFromAccount] = useState(null);
  const [toAccount, setToAccount] = useState(null);
  const [transferAmount, setTransferAmount] = useState(0);

  // SELECTORS + LOCAL VARIABLES
  const verifiedFundingAccounts = useAppSelector(fundingAccountsSelectors.getVerifiedFundingAccountsByAccountId);

  const enoughFundingAccountsForTransfers = verifiedFundingAccounts?.size >= 2;
  const hasTransfers = useAppSelector(a2aTransfersSelectors.getHasTransfers);

  const showTransferMoneyCard = hasTransfers || verifiedFundingAccounts?.size >= 1;

  // On mount
  useEffect(() => {
    tracker.track(tracker.events.viewTransferActivity);
    dispatch(a2aTransfersActions.getA2aTransfers());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // CALLBACKS
  const onDialogClose = useCallback((isError) => {
    if (!isError) {
      setFromAccount(null);
      setToAccount(null);
      setTransferAmount(0);
    }
  }, []);

  const kickoffTransferFlow = useCallback(() => {
    if (fromAccount && toAccount && transferAmount) {
      dispatch(
        showInitiateTransferDialog({
          initiator: 'transfer activity widget',
          startingIndex: transferDialogIndexEnums.SUBMIT,
          transferOptions: {
            fromAccount,
            toAccount,
            transferAmount,
          },
          disableBack: true,
          onClose: onDialogClose,
        }),
      );
    } else {
      dispatch(
        showInitiateTransferDialog({
          initiator: 'transfer activity widget',
        }),
      );
    }
  }, [dispatch, fromAccount, onDialogClose, toAccount, transferAmount]);

  const noAccountsState = useMemo(
    () => (
      <Box className={classes.zeroStyles}>
        <ZeroStateView
          animate
          id='transfer-no-accounts-state'
          title='Transfer money between accounts'
          description='Move money between your accounts right from Simplifi!'
          qIllustration='transfers'
          button={
            <Button
              id={'setup-accounts-button'}
              variant='contained'
              onClick={() => {
                if (enoughFundingAccountsForTransfers) {
                  kickoffTransferFlow();
                } else {
                  kickoffA2AFlow(kickoffObj);
                }
              }}
            >
              {enoughFundingAccountsForTransfers ? 'Transfer Money' : 'Set Up Transfers'}
            </Button>
          }
        />
      </Box>
    ),
    [classes.zeroStyles, enoughFundingAccountsForTransfers, kickoffA2AFlow, kickoffTransferFlow],
  );

  return (
    <div className={classes.root}>
      <SettingsHeader title='Transfer activity' />
      <Stack direction='row' gap={2} className={classes.container} padding={0.25}>
        {hasTransfers ? <TransferList /> : <Paper className={classes.errorRoot}>{noAccountsState}</Paper>}
        {showTransferMoneyCard ? (
          <div className={classes.rightWrapper}>
            <Paper className={classes.rightCardWrapper}>
              <TransferAccountSelect
                historyPage
                fromAccount={fromAccount}
                setFromAccount={setFromAccount}
                toAccount={toAccount}
                setToAccount={setToAccount}
                transferAmount={transferAmount}
                setTransferAmount={setTransferAmount}
                onConfirm={kickoffTransferFlow}
              />
            </Paper>
          </div>
        ) : null}
      </Stack>
    </div>
  );
};

export default TransferHistory;
